import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { PortalFillSchema } from "./fill"
import { z } from "zod"

import {
	INTERNAL_ROLES,
	ORDER_COMMAND_TYPES,
	ORDER_STATUSES,
	PATIENT_STEP_IDS,
	patientWorkflow,
	type PatientWorkflowEventType,
} from "@optimist/workflow-engine"
import { RoleKeySchema, WorkflowActorRoleKeySchema } from "./authz"
import { AddressIdSchema, CheckoutOfferingIdSchema, OrderIdSchema } from "./common"
import {
	JourneyDefinitionIdSchema,
	WorkflowTemplateIdSchema,
} from "./journey-definitions"
import { VisitTypeSchema } from "./product"
import {
	QuestionnaireAnswersSchema,
	QuestionnaireTemplateIdSchema,
} from "./questionnaire"
import { CheckoutPricingSummarySchema } from "./pricing"
import { IntegrationCaseStatusSchema, LabUploadDocumentSchema } from "./lab"
import { OrderMembershipSchema, PortalOrderMembershipSchema } from "./membership"
import { createRequiredPersonNameSchema } from "./person-name"
import { PatientTimezoneSchema, TelehealthVisitAccessUrlSchema } from "./telehealth"

export const OrderStatusSchema = z.enum(ORDER_STATUSES).meta({ id: "OrderStatus" })

export type OrderStatus = z.infer<typeof OrderStatusSchema>

export const OrderDisplayStatusSchema = z.enum([...ORDER_STATUSES, "ON_HOLD"]).meta({ id: "OrderDisplayStatus" })
export type OrderDisplayStatus = z.infer<typeof OrderDisplayStatusSchema>

export const OrderCommandTypeSchema = z.enum(ORDER_COMMAND_TYPES).meta({ id: "OrderCommandType" })

export type OrderCommandType = z.infer<typeof OrderCommandTypeSchema>

export const InternalRoleSchema = z.enum(INTERNAL_ROLES).meta({ id: "InternalRole" })

export type InternalRole = z.infer<typeof InternalRoleSchema>

export const ActorTypeSchema = z.enum(["internal", "patient"]).meta({ id: "ActorType" })

export type ActorType = z.infer<typeof ActorTypeSchema>

export const OrderLabFulfillmentMethodSchema = z
	.enum(["ship_kit", "upload_existing_results"])
	.meta({ id: "OrderLabFulfillmentMethod" })

export type OrderLabFulfillmentMethod = z.infer<typeof OrderLabFulfillmentMethodSchema>

export const OrderEventActorRoleSchema = z.union([RoleKeySchema, WorkflowActorRoleKeySchema])

export const PatientStepIdSchema = z.enum(PATIENT_STEP_IDS).meta({ id: "PatientStepId" })

export type PatientStepId = z.infer<typeof PatientStepIdSchema>

export const PatientWorkflowStateValueSchema = z.enum(patientWorkflow.stateValues).meta({
	id: "PatientWorkflowStateValue",
})

export type PatientWorkflowStateValue = z.infer<typeof PatientWorkflowStateValueSchema>

export const PatientWorkflowContextSchema = z
	.strictObject({
		labFulfillmentMethod: OrderLabFulfillmentMethodSchema.nullable(),
		kitLabUploadPayment: z.enum(["paid", "unpaid"]).optional(),
	})
	.meta({ id: "PatientWorkflowContext" })

export type PatientWorkflowContext = z.infer<typeof PatientWorkflowContextSchema>

export const PatientWorkflowCheckpointSchema = z
	.strictObject({
		machineId: z.literal(patientWorkflow.machineId),
		machineVersion: z.literal(patientWorkflow.machineVersion),
		journeyDefinitionId: JourneyDefinitionIdSchema,
		workflowTemplateId: WorkflowTemplateIdSchema,
		workflowTemplateVersion: z.number().int().positive(),
		questionnaireTemplateId: QuestionnaireTemplateIdSchema,
		questionnaireTemplateVersion: z.string().min(1),
		value: PatientWorkflowStateValueSchema,
		context: PatientWorkflowContextSchema,
		revision: z.number().int().nonnegative(),
	})
	.meta({ id: "PatientWorkflowCheckpoint" })

export type PatientWorkflowCheckpoint = z.infer<typeof PatientWorkflowCheckpointSchema>

export const PatientWorkflowOrderSchema = z
	.strictObject({
		id: OrderIdSchema,
		currentStatus: OrderStatusSchema,
		workflow: PatientWorkflowCheckpointSchema,
	})
	.meta({ id: "PatientWorkflowOrder" })

export type PatientWorkflowOrder = z.infer<typeof PatientWorkflowOrderSchema>

export const PatientWorkflowOrderResponseSchema = z
	.strictObject({
		order: PatientWorkflowOrderSchema,
	})
	.meta({ id: "PatientWorkflowOrderResponse" })

export type PatientWorkflowOrderResponse = z.infer<typeof PatientWorkflowOrderResponseSchema>

export const OrderShippingSnapshotSchema = z
	.object({
		recipientFirstName: z.string().min(1),
		recipientLastName: z.string().min(1),
		street1: z.string().min(1),
		street2: z.string().nullable().optional(),
		city: z.string().min(1),
		region: z.string().min(1),
		postalCode: z.string().min(1),
		countryCode: z.string().apply(utf16Length("exact", 2)),
		notes: z.string().nullable().optional(),
	})
	.meta({ id: "OrderShippingSnapshot" })

export type OrderShippingSnapshot = z.infer<typeof OrderShippingSnapshotSchema>

export const AgreementDocumentIdSchema = z
	.enum([
		"membership-agreement",
		"testosterone-agreement",
		"washington-consumer-health-data-privacy",
		"telehealth-informed-consent",
	])
	.meta({ id: "AgreementDocumentId" })

export type AgreementDocumentId = z.infer<typeof AgreementDocumentIdSchema>

export const ORDER_AGREEMENT_DEFINITIONS = {
	telehealthConsent: {
		documentId: "telehealth-informed-consent",
		documentVersion: "2026-05-13",
	},
	membershipAgreement: {
		documentId: "membership-agreement",
		documentVersion: "2026-05-14",
	},
	testosteroneAgreement: {
		documentId: "testosterone-agreement",
		documentVersion: "2026-03-26",
	},
	washingtonConsumerHealthDataPrivacy: {
		documentId: "washington-consumer-health-data-privacy",
		documentVersion: "2026-05-13",
	},
} as const satisfies Readonly<
	Record<string, { readonly documentId: AgreementDocumentId; readonly documentVersion: string }>
>

export type OrderAgreementKey = keyof typeof ORDER_AGREEMENT_DEFINITIONS

export const OrderAgreementAcceptanceSchema = z
	.object({
		accepted: z.literal(true),
		signedFullName: z.string().min(1),
		acceptedAt: IsoTimestampSchema,
		documentId: AgreementDocumentIdSchema,
		documentVersion: z.string().min(1),
	})
	.meta({ id: "OrderAgreementAcceptance" })

export type OrderAgreementAcceptance = z.infer<typeof OrderAgreementAcceptanceSchema>

export const OrderAgreementsSchema = z
	.object({
		telehealthConsent: OrderAgreementAcceptanceSchema.optional(),
		membershipAgreement: OrderAgreementAcceptanceSchema.optional(),
		testosteroneAgreement: OrderAgreementAcceptanceSchema.optional(),
		washingtonConsumerHealthDataPrivacy: OrderAgreementAcceptanceSchema.optional(),
	})
	.meta({ id: "OrderAgreements" })

export type OrderAgreements = z.infer<typeof OrderAgreementsSchema>

export const OrderSchema = z
	.object({
		id: z.uuid(),
		patientUserId: z.string().optional(),
		patientProfileId: z.uuid().optional(),
		patientFirstName: z.string().optional(),
		patientLastName: z.string().optional(),
		journeyDefinitionId: JourneyDefinitionIdSchema,
		journeyDefinitionVersion: z.number().int().positive(),
		visitType: VisitTypeSchema.nullable().optional(),
		selectedOfferingId: CheckoutOfferingIdSchema.nullable().optional(),
		labFulfillmentMethod: OrderLabFulfillmentMethodSchema.nullable().optional(),
		shippingAddressId: AddressIdSchema.nullable().optional(),
		shippingSnapshot: OrderShippingSnapshotSchema.nullable().optional(),
		currentStatus: OrderStatusSchema,
		metadata: z.record(z.string(), z.unknown()),
		intake: z.lazy(() => OrderIntakeSchema).nullable().optional(),
		patientProfile: z.lazy(() => OrderPatientProfilePatchSchema).nullable().optional(),
		workflow: PatientWorkflowCheckpointSchema.optional(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
		pricing: CheckoutPricingSummarySchema.optional(),
	})
	.meta({ id: "Order" })

export type Order = z.infer<typeof OrderSchema>

export const PortalOrderListItemSchema = z
	.strictObject({
		id: OrderIdSchema,
		patientFirstName: z.string().optional(),
		patientLastName: z.string().optional(),
		journeyDefinitionId: JourneyDefinitionIdSchema,
		journeyDefinitionVersion: z.number().int().positive(),
		currentStatus: OrderStatusSchema,
		displayStatus: OrderDisplayStatusSchema,
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
		treatment: z.string().nullable(),
	})
	.meta({ id: "PortalOrderListItem" })

export type PortalOrderListItem = z.infer<typeof PortalOrderListItemSchema>

export const PortalOrderShippingAddressSchema = z
	.strictObject({
		recipientFirstName: z.string().min(1),
		recipientLastName: z.string(),
		street1: z.string().min(1),
		street2: z.string().nullable().optional(),
		city: z.string().min(1),
		region: z.string().min(1),
		postalCode: z.string().min(1),
		countryCode: z.string().apply(utf16Length("exact", 2)),
	})
	.meta({ id: "PortalOrderShippingAddress" })

export type PortalOrderShippingAddress = z.infer<typeof PortalOrderShippingAddressSchema>

export const PortalOrderDetailSchema = z
	.strictObject({
		id: OrderIdSchema,
		patientUserId: z.string().optional(),
		patientProfileId: z.uuid().optional(),
		patientFirstName: z.string().optional(),
		patientLastName: z.string().optional(),
		journeyDefinitionId: JourneyDefinitionIdSchema,
		journeyDefinitionVersion: z.number().int().positive(),
		visitType: VisitTypeSchema.nullable().optional(),
		selectedOfferingId: CheckoutOfferingIdSchema.nullable().optional(),
		labFulfillmentMethod: OrderLabFulfillmentMethodSchema.nullable().optional(),
		currentStatus: OrderStatusSchema,
		displayStatus: OrderDisplayStatusSchema,
		workflow: PatientWorkflowCheckpointSchema,
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
		pricing: CheckoutPricingSummarySchema.optional(),
		intake: z.lazy(() => OrderIntakeSchema).optional(),
		patientProfile: z.lazy(() => OrderPatientProfilePatchSchema).optional(),
		shippingAddress: PortalOrderShippingAddressSchema.optional(),
	})
	.meta({ id: "PortalOrderDetail" })

export type PortalOrderDetail = z.infer<typeof PortalOrderDetailSchema>

export const PortalOrderVisitSchema = z
	.strictObject({
		eventType: z.string(),
		visitId: z.string(),
		visitStatus: z.string(),
		scheduledAt: IsoTimestampSchema.optional(),
		scheduledAtMillis: z.number().int().positive().optional(),
		patientTimezone: PatientTimezoneSchema.optional(),
		providerId: z.string().optional(),
		visitAccessUrl: TelehealthVisitAccessUrlSchema.optional(),
		visitAccessExpiresAt: IsoTimestampSchema.optional(),
		cancellationReason: z.string().optional(),
		requiredActions: z.array(z.string()).optional(),
		rescheduledAt: IsoTimestampSchema.optional(),
		startedAt: IsoTimestampSchema.optional(),
		patientJoinedAt: IsoTimestampSchema.optional(),
		providerJoinedAt: IsoTimestampSchema.optional(),
		attendedAt: IsoTimestampSchema.optional(),
		chartReviewId: z.string().optional(),
		decision: z.enum(["approved", "denied"]).optional(),
		reviewCompletedAt: IsoTimestampSchema.optional(),
	})
	.meta({ id: "PortalOrderVisit" })

export type PortalOrderVisit = z.infer<typeof PortalOrderVisitSchema>

export const PortalOrderTrackingSchema = z
	.strictObject({
		medicationTrackingNumber: z.string().nullable(),
		pharmacy: z.string().nullable(),
		shippedAt: IsoTimestampSchema.nullable(),
		deliveredAt: IsoTimestampSchema.nullable(),
		kitTrackingUrl: z.url().nullable(),
		sampleTrackingUrl: z.url().nullable(),
	})
	.meta({ id: "PortalOrderTracking" })

export type PortalOrderTracking = z.infer<typeof PortalOrderTrackingSchema>

export const PortalOrderLineItemSchema = z
	.strictObject({
		id: z.uuid(),
		orderId: OrderIdSchema,
		productId: z.uuid().nullable(),
		quantity: z.number().int().positive(),
		skuSnapshot: z.string().nullable(),
		nameSnapshot: z.string().nullable(),
		descriptionSnapshot: z.string().nullable(),
		unitAmountCentsSnapshot: z.number().int().nullable(),
		currencySnapshot: z.string().nullable(),
		productNdc: z.string().nullable(),
		productSku: z.string().nullable(),
		productName: z.string().nullable(),
		productDescription: z.string().nullable(),
	})
	.meta({ id: "PortalOrderLineItem" })

export type PortalOrderLineItem = z.infer<typeof PortalOrderLineItemSchema>

export const PortalOrderSelectedOfferingSchema = z
	.strictObject({
		id: CheckoutOfferingIdSchema,
		name: z.string(),
		description: z.string().nullable(),
		unitAmountCents: z.number().int(),
		currency: z.string(),
		code: z.string().nullable(),
	})
	.meta({ id: "PortalOrderSelectedOffering" })

export type PortalOrderSelectedOffering = z.infer<typeof PortalOrderSelectedOfferingSchema>

export const PortalGenHealthProductOrderSchema = z
	.strictObject({
		caseId: z.uuid(),
		status: IntegrationCaseStatusSchema,
		patientId: z.string().nullable(),
		patientIdPresent: z.boolean(),
		productOrderId: z.string().nullable(),
		lastFailureAt: IsoTimestampSchema.nullable(),
		lastErrorMessage: z.string().nullable(),
		lastErrorCode: z.string().nullable(),
		canRetry: z.boolean(),
	})
	.meta({ id: "PortalGenHealthProductOrder" })

export type PortalGenHealthProductOrder = z.infer<typeof PortalGenHealthProductOrderSchema>

export const PortalGenHealthIntakeSubmissionSchema = z
	.strictObject({
		caseId: z.uuid(),
		status: IntegrationCaseStatusSchema,
		formKey: z.string().nullable(),
		formId: z.string().nullable(),
		submissionId: z.string().nullable(),
		formsCompletionStatus: z.string().nullable(),
		lastFailureAt: IsoTimestampSchema.nullable(),
		lastErrorMessage: z.string().nullable(),
		lastErrorCode: z.string().nullable(),
		canReview: z.boolean(),
	})
	.meta({ id: "PortalGenHealthIntakeSubmission" })

export type PortalGenHealthIntakeSubmission = z.infer<
	typeof PortalGenHealthIntakeSubmissionSchema
>

export const PortalOrderMutationSchema = z
	.strictObject({
		id: OrderIdSchema,
		currentStatus: OrderStatusSchema,
	})
	.meta({ id: "PortalOrderMutation" })

export type PortalOrderMutation = z.infer<typeof PortalOrderMutationSchema>

export const PortalOrderMutationResponseSchema = z
	.strictObject({
		order: PortalOrderMutationSchema,
	})
	.meta({ id: "PortalOrderMutationResponse" })

export type PortalOrderMutationResponse = z.infer<typeof PortalOrderMutationResponseSchema>

export const PortalIntegrationSyncResultSchema = z
	.strictObject({
		success: z.boolean(),
		stage: z.enum(["config", "build_payload", "create_lab_order"]).optional(),
		adapterKey: z.enum(["gen_health", "spot"]).optional(),
		message: z.string().optional(),
	})
	.meta({ id: "PortalIntegrationSyncResult" })

export type PortalIntegrationSyncResult = z.infer<typeof PortalIntegrationSyncResultSchema>

export const PortalPaymentIntegrationMutationResponseSchema = z
	.strictObject({
		order: PortalOrderMutationSchema,
		integration: PortalIntegrationSyncResultSchema,
	})
	.meta({ id: "PortalPaymentIntegrationMutationResponse" })

export type PortalPaymentIntegrationMutationResponse = z.infer<
	typeof PortalPaymentIntegrationMutationResponseSchema
>

export const PortalLabUploadDocumentDecisionRequestSchema = z
	.strictObject({ reason: z.string().trim().min(1).apply(utf16Length("max", 1000)) })
	.meta({ id: "PortalLabUploadDocumentDecisionRequest" })

export type PortalLabUploadDocumentDecisionRequest = z.infer<
	typeof PortalLabUploadDocumentDecisionRequestSchema
>

export const PortalLabUploadDocumentActionResponseSchema = z
	.strictObject({ labUploadDocumentId: z.uuid() })
	.meta({ id: "PortalLabUploadDocumentActionResponse" })

export type PortalLabUploadDocumentActionResponse = z.infer<
	typeof PortalLabUploadDocumentActionResponseSchema
>

export const OrderCommandSchema = z
	.object({
		commandType: OrderCommandTypeSchema,
		reason: z.string().optional(),
		metadata: z.record(z.string(), z.unknown()).optional(),
	})
	.meta({ id: "OrderCommand" })

export type OrderCommand = z.infer<typeof OrderCommandSchema>

export const OrderEventSchema = z
	.object({
		id: z.string(),
		orderId: z.uuid(),
		eventType: z.string(),
		fromStatus: OrderStatusSchema,
		toStatus: OrderStatusSchema,
		payload: z.record(z.string(), z.unknown()),
		actorUserId: z.string(),
		actorRoleKey: OrderEventActorRoleSchema.nullable().optional(),
		actorOrganizationId: z.uuid().nullable().optional(),
		actorRegionId: z.uuid().nullable().optional(),
		actorClinicId: z.uuid().nullable().optional(),
		actorType: ActorTypeSchema,
		createdAt: IsoTimestampSchema,
	})
	.meta({ id: "OrderEvent" })

export type OrderEvent = z.infer<typeof OrderEventSchema>

export const PortalOrderEventSchema = z
	.object({
		id: z.string(),
		orderId: z.uuid(),
		eventType: z.string(),
		fromStatus: OrderStatusSchema,
		toStatus: OrderStatusSchema,
		actorRoleKey: OrderEventActorRoleSchema.nullable().optional(),
		actorOrganizationId: z.uuid().nullable().optional(),
		actorRegionId: z.uuid().nullable().optional(),
		actorClinicId: z.uuid().nullable().optional(),
		actorType: ActorTypeSchema,
		createdAt: IsoTimestampSchema,
	})
	.meta({ id: "PortalOrderEvent" })

export type PortalOrderEvent = z.infer<typeof PortalOrderEventSchema>

export const OrderCapabilitiesResponseSchema = z
	.object({
		orderId: z.uuid(),
		blocked: z.boolean(),
		blockerKind: z.string().optional(),
		allowedCommands: z.array(OrderCommandTypeSchema),
	})
	.meta({ id: "OrderCapabilitiesResponse" })

export type OrderCapabilitiesResponse = z.infer<typeof OrderCapabilitiesResponseSchema>

export const SubmittedQuestionnaireDataSchema = z
	.strictObject({
		templateId: QuestionnaireTemplateIdSchema,
		templateVersion: z.string().min(1),
		answers: QuestionnaireAnswersSchema,
	})
	.meta({ id: "SubmittedQuestionnaireData" })

export type SubmittedQuestionnaireData = z.infer<typeof SubmittedQuestionnaireDataSchema>

export const CreateOrderRequestSchema = z
	.strictObject({
		assignmentToken: z.string().min(1),
		questionnaire: SubmittedQuestionnaireDataSchema,
	})
	.meta({ id: "CreateOrderRequest" })

export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>

export const UpdateOrderSelectionRequestSchema = z
	.object({
		selectedOfferingId: CheckoutOfferingIdSchema,
	})
	.meta({ id: "UpdateOrderSelectionRequest" })

export type UpdateOrderSelectionRequest = z.infer<typeof UpdateOrderSelectionRequestSchema>

export const UpdateOrderLabFulfillmentRequestSchema = z
	.object({
		labFulfillmentMethod: OrderLabFulfillmentMethodSchema,
	})
	.meta({ id: "UpdateOrderLabFulfillmentRequest" })

export type UpdateOrderLabFulfillmentRequest = z.infer<typeof UpdateOrderLabFulfillmentRequestSchema>

export const OrderPhotoIdSlotKeySchema = z.enum(["idFront", "idBack", "selfie"]).meta({ id: "OrderPhotoIdSlotKey" })

export type OrderPhotoIdSlotKey = z.infer<typeof OrderPhotoIdSlotKeySchema>

export const OrderPhotoIdSlotStateSchema = z
	.object({
		key: OrderPhotoIdSlotKeySchema,
		uploaded: z.boolean(),
	})
	.meta({ id: "OrderPhotoIdSlotState" })

export type OrderPhotoIdSlotState = z.infer<typeof OrderPhotoIdSlotStateSchema>

export const OrderPhotoIdStatusResponseSchema = z
	.object({
		orderId: OrderIdSchema,
		requiredActions: z.array(z.string()),
		uploadsRequired: z.boolean(),
		isComplete: z.boolean(),
		patientId: z.string().min(1).nullable(),
		sessionSlotKey: z.string().nullable(),
		slots: z.object({
			idFront: OrderPhotoIdSlotStateSchema,
			idBack: OrderPhotoIdSlotStateSchema,
			selfie: OrderPhotoIdSlotStateSchema,
		}),
	})
	.meta({ id: "OrderPhotoIdStatusResponse" })

export type OrderPhotoIdStatusResponse = z.infer<typeof OrderPhotoIdStatusResponseSchema>

export const PortalOrderPhotoIdStatusSchema = z
	.object({
		linked: z.boolean(),
		isComplete: z.boolean(),
		uploadsRequired: z.boolean(),
		slots: z.object({
			idFront: OrderPhotoIdSlotStateSchema,
			idBack: OrderPhotoIdSlotStateSchema,
			selfie: OrderPhotoIdSlotStateSchema,
		}),
	})
	.meta({ id: "PortalOrderPhotoIdStatus" })

export type PortalOrderPhotoIdStatus = z.infer<typeof PortalOrderPhotoIdStatusSchema>

export const PortalOrderDetailResponseSchema = z
	.strictObject({
		order: PortalOrderDetailSchema.extend({
			activeFill: PortalFillSchema.nullable().optional(),
			uploadedLabDocuments: z.array(LabUploadDocumentSchema).optional(),
			visit: PortalOrderVisitSchema.optional(),
			tracking: PortalOrderTrackingSchema.optional(),
			photoIdStatus: PortalOrderPhotoIdStatusSchema.optional(),
			selectedOffering: PortalOrderSelectedOfferingSchema.optional(),
			genHealthProductOrder: PortalGenHealthProductOrderSchema.optional(),
			genHealthIntakeSubmission: PortalGenHealthIntakeSubmissionSchema.optional(),
		}),
		events: z.array(PortalOrderEventSchema).optional(),
		lineItems: z.array(PortalOrderLineItemSchema).optional(),
		membership: PortalOrderMembershipSchema.optional(),
	})
	.meta({ id: "InternalOrderDetailResponse" })

export type PortalOrderDetailResponse = z.infer<typeof PortalOrderDetailResponseSchema>

export const OrderPhotoIdUploadResponseSchema = z
	.object({
		orderId: OrderIdSchema,
		uploadedSlot: OrderPhotoIdSlotStateSchema,
		status: OrderPhotoIdStatusResponseSchema,
		order: PatientWorkflowOrderSchema,
	})
	.meta({ id: "OrderPhotoIdUploadResponse" })

export type OrderPhotoIdUploadResponse = z.infer<typeof OrderPhotoIdUploadResponseSchema>

export const OrderLabResultsUploadResponseSchema = z
	.discriminatedUnion("status", [
		z.object({
			orderId: z.uuid(),
			labUploadDocumentId: z.uuid(),
			status: z.literal("accepted_for_review"),
			nextStep: z.literal("next-steps"),
			order: PatientWorkflowOrderSchema,
		}),
		z.object({
			orderId: z.uuid(),
			labUploadDocumentId: z.uuid(),
			status: z.literal("human_review_requested"),
			nextStep: z.literal("next-steps"),
			order: PatientWorkflowOrderSchema,
		}),
		z.object({
			orderId: z.uuid(),
			labUploadDocumentId: z.uuid(),
			status: z.literal("eligibility_failed"),
			nextStep: z.literal("lab-results"),
			order: PatientWorkflowOrderSchema,
		}),
	])
	.meta({ id: "OrderLabResultsUploadResponse" })

export type OrderLabResultsUploadResponse = z.infer<typeof OrderLabResultsUploadResponseSchema>

export const OrderLabResultsReviewRequestResponseSchema = z
	.object({
		orderId: z.uuid(),
		labUploadDocumentId: z.uuid(),
		supportTicketId: z.uuid(),
		status: z.literal("human_review_requested"),
		order: PatientWorkflowOrderSchema,
	})
	.meta({ id: "OrderLabResultsReviewRequestResponse" })

export type OrderLabResultsReviewRequestResponse = z.infer<typeof OrderLabResultsReviewRequestResponseSchema>

export const OrderPatientProfileSchema = z
	.object({
		dateOfBirth: z.string().min(1),
		state: z.string().min(1),
		consentToTelehealth: z.boolean(),
		gender: z.string().min(1).optional(),
	})
	.meta({ id: "OrderPatientProfile" })

export type OrderPatientProfile = z.infer<typeof OrderPatientProfileSchema>

export const OrderPatientProfilePatchSchema = z
	.object({
		dateOfBirth: z.string().min(1).optional(),
		state: z.string().min(1).optional(),
		consentToTelehealth: z.boolean().optional(),
		gender: z.string().min(1).optional(),
	})
	.refine((value) => Object.keys(value).length > 0, {
		error: "At least one patient profile field is required.",
	})
	.meta({ id: "OrderPatientProfilePatch" })

export type OrderPatientProfilePatch = z.infer<typeof OrderPatientProfilePatchSchema>

export const OrderIntakeQuestionnaireStatusSchema = z
	.enum(["in_progress", "complete"])
	.meta({ id: "OrderIntakeQuestionnaireStatus" })

export type OrderIntakeQuestionnaireStatus = z.infer<typeof OrderIntakeQuestionnaireStatusSchema>

export const OrderIntakeQuestionnaireSubmissionSchema = z
	.object({
		...SubmittedQuestionnaireDataSchema.shape,
		status: OrderIntakeQuestionnaireStatusSchema.optional(),
		updatedAt: IsoTimestampSchema.optional(),
	})
	.meta({ id: "OrderIntakeQuestionnaireSubmission" })

export type OrderIntakeQuestionnaireSubmission = z.infer<typeof OrderIntakeQuestionnaireSubmissionSchema>

export const OrderIntakeSchema = z
	.object({
		agreements: OrderAgreementsSchema.default({}),
		questionnaires: z.record(z.string(), OrderIntakeQuestionnaireSubmissionSchema).default({}),
	})
	.meta({ id: "OrderIntake" })

export type OrderIntake = z.infer<typeof OrderIntakeSchema>

export const ORDER_INTAKE_WORKFLOW_EVENT_TYPES = [
	"intake.updated",
	"gender.selected",
	"profile.completed",
	"membership_agreement.accepted",
	"testosterone_agreement.accepted",
	"clinical_review.completed",
] as const satisfies readonly PatientWorkflowEventType[]

export const OrderIntakeWorkflowEventSchema = z
	.enum(ORDER_INTAKE_WORKFLOW_EVENT_TYPES)
	.meta({ id: "OrderIntakeWorkflowEvent" })

export type OrderIntakeWorkflowEvent = z.infer<typeof OrderIntakeWorkflowEventSchema>

export const UpdateOrderIntakeRequestSchema = z
	.strictObject({
		intake: OrderIntakeSchema,
		workflowEvent: OrderIntakeWorkflowEventSchema,
		patientProfile: OrderPatientProfilePatchSchema.optional(),
	})
	.meta({ id: "UpdateOrderIntakeRequest" })

export type UpdateOrderIntakeRequest = z.infer<typeof UpdateOrderIntakeRequestSchema>

export const OrderEligibilityScreeningRequestSchema = z
	.strictObject({
		hasAnyHardDisqualifier: z.boolean(),
	})
	.meta({ id: "OrderEligibilityScreeningRequest" })

export type OrderEligibilityScreeningRequest = z.infer<typeof OrderEligibilityScreeningRequestSchema>

export const OrderEligibilityScreeningResponseSchema = z
	.discriminatedUnion("status", [
		z.object({
			orderId: z.uuid(),
			currentStatus: OrderStatusSchema,
			status: z.literal("passed"),
			nextStep: z.literal("post-auth"),
			order: PatientWorkflowOrderSchema,
		}),
		z.object({
			orderId: z.uuid(),
			currentStatus: OrderStatusSchema,
			status: z.literal("failed"),
			nextStep: z.literal("eligibility-failed"),
			order: PatientWorkflowOrderSchema,
			failure: z.object({
				questionId: z.string().min(1),
				question: z.string().min(1),
				selectedAnswer: z.string().min(1),
				riskLevel: z.literal("red"),
			}),
		}),
	])
	.meta({ id: "OrderEligibilityScreeningResponse" })

export type OrderEligibilityScreeningResponse = z.infer<typeof OrderEligibilityScreeningResponseSchema>

export const OrderIntakeResetResponseSchema = z
	.strictObject({
		order: PatientWorkflowOrderSchema,
		nextStep: PatientStepIdSchema,
	})
	.meta({ id: "OrderIntakeResetResponse" })

export type OrderIntakeResetResponse = z.infer<typeof OrderIntakeResetResponseSchema>

export const ORDER_SHIPPING_NOTES_MAX_LENGTH = 10_000

export const SetOrderShippingAddressRequestSchema = z
	.object({
		addressId: AddressIdSchema,
		recipientFirstName: createRequiredPersonNameSchema("First name").optional(),
		recipientLastName: createRequiredPersonNameSchema("Last name").optional(),
		notes: z.string().trim().apply(utf16Length("max", ORDER_SHIPPING_NOTES_MAX_LENGTH)).optional(),
	})
	.meta({ id: "SetOrderShippingAddressRequest" })

export type SetOrderShippingAddressRequest = z.infer<typeof SetOrderShippingAddressRequestSchema>

export const ActionRequestSchema = z
	.object({
		reason: z.string().optional(),
		metadata: z.record(z.string(), z.unknown()).optional(),
	})
	.meta({ id: "ActionRequest" })

export type ActionRequest = z.infer<typeof ActionRequestSchema>

export const BlockerOverrideRequestSchema = z
	.object({
		reason: z.string().min(1),
	})
	.meta({ id: "BlockerOverrideRequest" })

export type BlockerOverrideRequest = z.infer<typeof BlockerOverrideRequestSchema>

export const TimelineEventSchema = z
	.object({
		id: z.string(),
		type: z.string(),
		timestamp: IsoTimestampSchema,
		description: z.string(),
		actorId: z.string().optional(),
		actorName: z.string().optional(),
		metadata: z.record(z.string(), z.unknown()).optional(),
	})
	.meta({ id: "TimelineEvent" })

export type TimelineEvent = z.infer<typeof TimelineEventSchema>

export const ORDER_SORT_FIELDS = [
	"id",
	"patient",
	"treatment",
	"workflow",
	"status",
	"createdAt",
	"updatedAt",
] as const

export const OrderSortFieldSchema = z.enum(ORDER_SORT_FIELDS)
export const OrderSortDirectionSchema = z.enum(["asc", "desc"])

export type OrderSortField = z.infer<typeof OrderSortFieldSchema>
export type OrderSortDirection = z.infer<typeof OrderSortDirectionSchema>

export const DEFAULT_ORDER_SORT = {
	sortBy: "updatedAt",
	sortDirection: "desc",
} as const satisfies {
	readonly sortBy: OrderSortField
	readonly sortDirection: OrderSortDirection
}

export const OrderFiltersSchema = z
	.object({
		orderId: z.uuid().optional(),
		search: z.string().trim().min(1).apply(utf16Length("max", 100)).optional(),
		status: OrderDisplayStatusSchema.optional(),
		journeyDefinitionId: JourneyDefinitionIdSchema.optional(),
		sortBy: OrderSortFieldSchema.default(DEFAULT_ORDER_SORT.sortBy),
		sortDirection: OrderSortDirectionSchema.default(DEFAULT_ORDER_SORT.sortDirection),
		page: z.coerce.number().int().positive().default(1),
		limit: z.coerce.number().int().positive().max(100).default(25),
	})
	.meta({ id: "OrderFilters" })

export type OrderFilters = z.infer<typeof OrderFiltersSchema>

export const OrderMembershipDetailSchema = z
	.object({
		order: OrderSchema,
		membership: OrderMembershipSchema.nullable(),
	})
	.meta({ id: "OrderMembershipDetail" })

export type OrderMembershipDetail = z.infer<typeof OrderMembershipDetailSchema>
